import React from "react";
import { Loader2, Banknote } from "lucide-react";
import { ModalShell, formatMoneyXOF } from "./helpers.js";
import { PAYMENT_METHODS, MOBILE_MONEY_PROVIDERS } from "../../constants/payment.js";

export default function PaymentModal({ show, onClose, paymentTarget, paymentForm, setPaymentForm, savingPayment, onSubmit }) {
  if (!show || !paymentTarget) return null;

  return (
    <ModalShell
      title="Enregistrer un paiement"
      subtitle={`${paymentTarget.title} - ${paymentTarget.customer_name || "Client"}`}
      onClose={onClose}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Résumé */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xs text-slate-500">Total TTC</div>
              <div className="text-sm font-bold text-slate-900">
                {formatMoneyXOF(paymentTarget.amount_ttc || paymentTarget.amount)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Déjà payé</div>
              <div className="text-sm font-bold text-emerald-600">
                {formatMoneyXOF(paymentTarget.paid_total || 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Reste dû</div>
              <div className="text-sm font-bold text-slate-900">
                {formatMoneyXOF(paymentTarget.due_total || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Montant + Méthode */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-900">Montant *</label>
            <input
              required
              type="number"
              min="0.01"
              step="0.01"
              max={paymentTarget.due_total || 999999}
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-900">Méthode</label>
            <select
              value={paymentForm.method}
              onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Opérateur Mobile Money */}
        {paymentForm.method === "mobile_money" && (
          <div>
            <label className="text-sm font-semibold text-slate-900">Opérateur Mobile Money</label>
            <select
              value={paymentForm.provider}
              onChange={(e) => setPaymentForm({ ...paymentForm, provider: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            >
              {MOBILE_MONEY_PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Référence + Date */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-900">Référence</label>
            <input
              type="text"
              value={paymentForm.reference}
              onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              placeholder="N° chèque, référence virement…"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-900">Date paiement</label>
            <input
              type="date"
              value={paymentForm.paid_at}
              onChange={(e) => setPaymentForm({ ...paymentForm, paid_at: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm font-semibold text-slate-900">Notes</label>
          <textarea
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            rows={2}
            placeholder="Commentaire optionnel…"
          />
        </div>

        {/* Boutons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={savingPayment}
            className="flex-1 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-70 inline-flex items-center justify-center gap-2"
          >
            {savingPayment ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Enregistrement…</>
            ) : (
              <><Banknote className="h-4 w-4" /> Enregistrer le paiement</>
            )}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
